/**
 * AI Dialog Component
 * Main interface for AI-powered OpenSCAD generation
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Settings,
  Sparkles,
  AlertCircle,
  CheckCircle,
  MessageSquare,
  Camera,
} from 'lucide-react';

import { AiMode, OpenRouterModel } from '@/types/ai';
import { useAiSettings } from '@/hooks/useAiSettings';
import { useAi } from '@/hooks/useAi';
import { createAiClient, fetchModels } from '@/services/ai';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Switch } from '@/components/ui/switch';
import { AiProgress } from './AiProgress';
import { AiTokenDisplay } from './AiTokenDisplay';
import { AiSettingsDialog } from './AiSettingsDialog';
import { ModelSelector } from './ModelSelector';
import { captureR3FCanvas } from '@/components/viewer/ScreenshotDialog';

interface AiDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentCode?: string;
  onCodeGenerated: (code: string) => void;
  compileCode?: (code: string) => Promise<{ success: boolean; error?: string }>;
  /** Pre-select the mode when opened from a slash command */
  initialMode?: AiMode;
  /** Pre-fill the prompt and auto-submit when opened from a slash command */
  initialPrompt?: string;
}

export function AiDialog({
  open,
  onOpenChange,
  currentCode,
  onCodeGenerated,
  compileCode,
  initialMode,
  initialPrompt,
}: AiDialogProps) {
  const {
    settings,
    isConfigured,
    setApiKey,
    setDefaultModel,
    setPromptTemplate,
    resetPromptTemplates,
  } = useAiSettings();

  const [mode, setMode] = useState<AiMode>('create');
  const [prompt, setPrompt] = useState('');
  const [selectedModel, setSelectedModel] = useState(settings.defaultModel);
  const [showSettings, setShowSettings] = useState(false);
  const [models, setModels] = useState<OpenRouterModel[]>([]);
  const [planFeedback, setPlanFeedback] = useState('');
  const [attachPreview, setAttachPreview] = useState(true);

  // Find the selected model info for cost calculation
  const selectedModelInfo = useMemo(
    () => models.find((m) => m.id === selectedModel) || null,
    [models, selectedModel],
  );

  const {
    state,
    isGenerating,
    canGenerate,
    tokenUsage,
    generate,
    approvePlan,
    requestPlanRevision,
    cancel,
    reset,
  } = useAi({
    promptTemplates: settings.promptTemplates,
    onCodeGenerated: (code) => {
      onCodeGenerated(code);
    },
    compileCode,
  });

  // Load models when dialog opens and API key is configured
  useEffect(() => {
    if (open && isConfigured) {
      const loadModels = async () => {
        try {
          const client = createAiClient(settings.apiKey);
          const fetchedModels = await fetchModels(client);
          setModels(fetchedModels);
        } catch (error) {
          console.error('Failed to load models:', error);
        }
      };
      loadModels();
    }
  }, [open, isConfigured, settings.apiKey]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      reset();
      setPrompt('');
      setPlanFeedback('');
    }
  }, [open, reset]);

  // Apply initialMode / initialPrompt when dialog opens
  useEffect(() => {
    if (open) {
      if (initialMode) setMode(initialMode);
      if (initialPrompt !== undefined) setPrompt(initialPrompt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Auto-submit when initialPrompt is provided and model/key are ready
  const autoSubmittedRef = useRef(false);
  // Stable ref to the latest generate function so the effect below can call it
  const handleGenerateRef = useRef<(() => Promise<void>) | null>(null);

  useEffect(() => {
    if (
      open &&
      initialPrompt &&
      initialPrompt.trim() &&
      selectedModel &&
      isConfigured &&
      canGenerate &&
      !isGenerating &&
      !autoSubmittedRef.current
    ) {
      autoSubmittedRef.current = true;
      // Small delay to let the dialog finish opening and prompt state to settle
      const timer = setTimeout(() => {
        handleGenerateRef.current?.();
      }, 200);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedModel, isConfigured, canGenerate]);

  // Reset auto-submit flag when dialog closes
  useEffect(() => {
    if (!open) autoSubmittedRef.current = false;
  }, [open]);

  // Sync default model from settings
  useEffect(() => {
    if (settings.defaultModel && !selectedModel) {
      setSelectedModel(settings.defaultModel);
    }
  }, [settings.defaultModel, selectedModel]);

  // Check if we can use modify mode
  const canModify = Boolean(currentCode && currentCode.trim().length > 0);

  // Handle mode change
  const handleModeChange = (newMode: string) => {
    if (newMode === 'modify' && !canModify) return;
    setMode(newMode as AiMode);
  };

  // Handle generate
  const handleGenerate = useCallback(async () => {
    if (!prompt.trim() || !selectedModel || !settings.apiKey) return;

    // Capture a screenshot of the current preview if the toggle is on
    let previewImageDataUrl: string | undefined;
    if (attachPreview) {
      const captured = captureR3FCanvas();
      if (captured) {
        previewImageDataUrl = captured;
      }
    }

    await generate(
      settings.apiKey,
      mode,
      prompt,
      selectedModel,
      selectedModelInfo,
      mode === 'modify' ? currentCode : undefined,
      previewImageDataUrl,
    );
  }, [
    prompt,
    selectedModel,
    settings.apiKey,
    mode,
    currentCode,
    selectedModelInfo,
    attachPreview,
    generate,
  ]);

  // Keep the ref in sync with the latest handleGenerate
  handleGenerateRef.current = handleGenerate;

  // Handle apply (when generation is complete)
  const handleApply = () => {
    if (state.generatedCode) {
      onCodeGenerated(state.generatedCode);
      onOpenChange(false);
    }
  };

  // Handle plan approval
  const handleApprovePlan = useCallback(() => {
    setPlanFeedback('');
    approvePlan();
  }, [approvePlan]);

  // Handle plan revision request
  const handleRequestRevision = useCallback(() => {
    if (!planFeedback.trim()) return;
    requestPlanRevision(planFeedback);
    setPlanFeedback('');
  }, [planFeedback, requestPlanRevision]);

  // Determine button state
  const isComplete = state.step === 'complete';
  const hasError = state.step === 'error';
  const isPlanApproval = state.step === 'planApproval';
  const canStartGeneration =
    canGenerate && prompt.trim() && selectedModel && isConfigured;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="!flex h-[90vh] max-h-[90vh] flex-col !gap-0 sm:max-w-[700px]">
          <DialogHeader className="flex-shrink-0 pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                <DialogTitle>AI Assistant</DialogTitle>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowSettings(true)}
                    className="h-8 w-8"
                  >
                    <Settings className="h-4 w-4" />
                    <span className="sr-only">Settings</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>AI Settings</TooltipContent>
              </Tooltip>
            </div>
            <DialogDescription>
              Generate or modify OpenSCAD code using AI
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="min-h-0 flex-1 py-4">
            <div className="flex flex-col gap-4 px-1 pr-3">
              {/* API Key Warning */}
              {!isConfigured && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Please configure your OpenRouter API key in{' '}
                    <button
                      onClick={() => setShowSettings(true)}
                      className="font-medium underline underline-offset-2"
                    >
                      settings
                    </button>{' '}
                    to use AI features.
                  </AlertDescription>
                </Alert>
              )}

              {/* Mode Selector */}
              {!isPlanApproval && (
                <Tabs value={mode} onValueChange={handleModeChange}>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="create">Create New</TabsTrigger>
                    <TabsTrigger value="modify" disabled={!canModify}>
                      Modify Current
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              )}

              {/* Model Selector */}
              {!isPlanApproval && (
                <div className="space-y-2">
                  <Label>Model</Label>
                  <ModelSelector
                    apiKey={settings.apiKey}
                    value={selectedModel}
                    onValueChange={(value) => {
                      setSelectedModel(value);
                      setDefaultModel(value);
                    }}
                    disabled={!isConfigured || isGenerating}
                  />
                </div>
              )}

              {/* Prompt Input */}
              {!isPlanApproval && (
                <div className="space-y-2">
                  <Label htmlFor="prompt">
                    {mode === 'create'
                      ? 'What would you like to create?'
                      : 'How would you like to modify the code?'}
                  </Label>
                  <Textarea
                    id="prompt"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder={
                      mode === 'create'
                        ? 'Describe the 3D object you want to create... e.g., "A parametric gear with adjustable tooth count, module size, and pressure angle"'
                        : 'Describe what changes you want... e.g., "Add rounded corners to all edges" or "Make the object hollow with adjustable wall thickness"'
                    }
                    rows={4}
                    disabled={!isConfigured || isGenerating}
                    className="resize-none"
                  />
                  <div className="flex items-center gap-2">
                    <Switch
                      id="attach-preview"
                      checked={attachPreview}
                      onCheckedChange={setAttachPreview}
                      disabled={!isConfigured || isGenerating}
                    />
                    <Label
                      htmlFor="attach-preview"
                      className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground"
                    >
                      <Camera className="h-3.5 w-3.5" />
                      Attach 3D preview screenshot
                    </Label>
                  </div>
                </div>
              )}

              {/* Progress Display */}
              {(isGenerating || isPlanApproval) && (
                <AiProgress
                  currentStep={state.step}
                  mode={mode}
                  bugfixAttempts={state.bugfixAttempts}
                />
              )}

              {/* Plan Approval Section */}
              {isPlanApproval && state.plan && (
                <div className="space-y-3 rounded-lg border border-primary/20 bg-primary/5 p-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-primary" />
                    <Label className="text-base font-semibold">
                      Review Generated Plan
                    </Label>
                  </div>
                  <ScrollArea className="h-[300px] rounded-md border border-border bg-background p-3">
                    <pre className="whitespace-pre-wrap font-mono text-xs">
                      {state.plan}
                    </pre>
                  </ScrollArea>
                  <div className="space-y-2">
                    <Label htmlFor="plan-feedback">
                      Request Changes (optional)
                    </Label>
                    <Textarea
                      id="plan-feedback"
                      value={planFeedback}
                      onChange={(e) => setPlanFeedback(e.target.value)}
                      placeholder="e.g., 'Please add a parameter for wall thickness' or 'Use a different approach for the rounded edges'"
                      rows={3}
                      className="resize-none"
                    />
                    <p className="text-xs text-muted-foreground">
                      You can approve the plan as-is or request changes before
                      implementation.
                    </p>
                  </div>
                </div>
              )}

              {/* Streaming Preview */}
              {(isGenerating || isComplete) &&
                state.currentContent &&
                !isPlanApproval && (
                  <div className="space-y-2">
                    <Label>
                      {isComplete ? 'Generated Code' : 'Generating...'}
                    </Label>
                    <ScrollArea className="h-[200px] rounded-md border border-border bg-muted/50 p-3">
                      <pre className="whitespace-pre-wrap font-mono text-xs">
                        {state.generatedCode || state.currentContent}
                      </pre>
                    </ScrollArea>
                  </div>
                )}

              {/* Error Display */}
              {hasError && state.error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{state.error}</AlertDescription>
                </Alert>
              )}

              {/* Compilation Warning */}
              {isComplete && state.compilationError && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    The generated code may have compilation issues:{' '}
                    {state.compilationError}
                  </AlertDescription>
                </Alert>
              )}

              {/* Token Usage */}
              {tokenUsage.totalTokens > 0 && (
                <AiTokenDisplay
                  tokenUsage={tokenUsage}
                  stepResults={state.results}
                />
              )}
            </div>
          </ScrollArea>

          <DialogFooter className="flex-shrink-0 gap-2 border-t pt-4 sm:gap-0">
            {isGenerating ? (
              <Button variant="outline" onClick={cancel}>
                Cancel
              </Button>
            ) : isPlanApproval ? (
              <>
                <Button variant="outline" onClick={cancel}>
                  Cancel
                </Button>
                {planFeedback.trim() && (
                  <Button variant="secondary" onClick={handleRequestRevision}>
                    <MessageSquare className="mr-2 h-4 w-4" />
                    Request Changes
                  </Button>
                )}
                <Button onClick={handleApprovePlan}>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Approve & Continue
                </Button>
              </>
            ) : isComplete ? (
              <>
                <Button variant="outline" onClick={() => reset()}>
                  Start Over
                </Button>
                <Button onClick={handleApply}>Apply Code</Button>
              </>
            ) : (
              <>
                <Button variant="ghost" onClick={() => onOpenChange(false)}>
                  Close
                </Button>
                <Button onClick={handleGenerate} disabled={!canStartGeneration}>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Generate
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <AiSettingsDialog
        open={showSettings}
        onOpenChange={setShowSettings}
        apiKey={settings.apiKey}
        onApiKeyChange={setApiKey}
        promptTemplates={settings.promptTemplates}
        onPromptTemplateChange={setPromptTemplate}
        onResetPromptTemplates={resetPromptTemplates}
      />
    </>
  );
}
